import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { SERVICES } from "@/data/site";

// User-uploaded card backdrops — cycled across the six service cards.
// Each backdrop is paired with a matching brand glow color for the hover state.
const CARD_BACKDROPS = [
  {
    img: "https://customer-assets-jai6qajn.emergentagent.net/job_ai-solutions-lab-22/artifacts/wzd6ihn6_ser3-1.png",
    glow: "56, 189, 248",   // sky
  },
  {
    img: "https://customer-assets-jai6qajn.emergentagent.net/job_ai-solutions-lab-22/artifacts/ms7wg1v4_ser3-2.png",
    glow: "129, 140, 248",  // indigo
  },
  {
    img: "https://customer-assets-jai6qajn.emergentagent.net/job_ai-solutions-lab-22/artifacts/wi1vte0g_ser3-3.png",
    glow: "168, 85, 247",   // violet
  },
];

export default function Services() {
  const scrollerRef = useRef(null);
  const pausedRef = useRef(false);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    // Pause the auto-scroll while the manual scroll animation plays,
    // otherwise the RAF loop overwrites scrollLeft every frame and the
    // arrow click appears to do nothing.
    pausedRef.current = true;
    el.scrollBy({ left: dir * (el.clientWidth * 0.9), behavior: "smooth" });
    clearTimeout(scrollBy._t);
    scrollBy._t = setTimeout(() => { pausedRef.current = false; }, 1200);
  };

  // Slow, seamless continuous auto-scroll. We render the item list TWICE
  // inside the scroller and imperceptibly reset scrollLeft by half the
  // scrollWidth once we cross the halfway mark — so the loop never jerks.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let raf = 0;
    let last = performance.now();
    let acc = 0; // sub-pixel accumulator
    const SPEED = 40; // px / second
    const tick = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (!pausedRef.current && document.visibilityState === "visible") {
        const half = el.scrollWidth / 2;
        if (half > 1) {
          acc += SPEED * dt;
          if (acc >= 1) {
            let next = el.scrollLeft + acc;
            // The duplicated content means positions `next` and `next - half`
            // render identically; when we cross the halfway mark, subtract
            // `half` to get back into the first copy without any visual jump.
            if (next >= half) next -= half;
            el.scrollLeft = next;
            acc = 0;
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <section className="relative py-8 md:py-12" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-muted-foreground">What we do</div>
            <h2 className="mt-4 font-heading font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              Our Technology Expertise
            </h2>
            <p className="mt-5 text-lg text-muted-foreground max-w-xl">
              Engineering intelligent solutions that transform businesses.
            </p>
          </div>
          <div className="flex gap-3">
            <button onClick={() => scrollBy(-1)} aria-label="Previous services"
              data-testid="services-prev"
              className="w-11 h-11 rounded-full border border-border hover:bg-muted transition-colors flex items-center justify-center">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button onClick={() => scrollBy(1)} aria-label="Next services"
              data-testid="services-next"
              className="w-11 h-11 rounded-full border border-border hover:bg-muted transition-colors flex items-center justify-center">
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          onMouseEnter={() => { pausedRef.current = true; }}
          onMouseLeave={() => { pausedRef.current = false; }}
          onFocusCapture={() => { pausedRef.current = true; }}
          onBlurCapture={() => { pausedRef.current = false; }}
          data-testid="services-scroller"
          className="flex gap-5 overflow-x-auto pb-6 -mx-2 px-2 no-scrollbar"
        >
          {[...SERVICES, ...SERVICES].map((s, i) => {
            const Icon = Icons[s.icon] || Icons.Sparkles;
            const bd = CARD_BACKDROPS[i % CARD_BACKDROPS.length];
            const isClone = i >= SERVICES.length;
            return (
              <motion.div
                key={`${s.slug}-${i}`}
                initial={isClone ? false : { opacity: 0, y: 24 }}
                whileInView={isClone ? undefined : { opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: (i % 4) * 0.06 }}
                aria-hidden={isClone ? "true" : undefined}
                className="shrink-0 w-[280px] md:w-[300px]"
              >
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  style={{ "--card-glow": bd.glow }}
                  className="group relative block card-clean p-6 h-[440px] flex flex-col overflow-hidden transition-shadow duration-500 hover:shadow-[0_0_48px_-4px_rgba(var(--card-glow),0.55)]"
                >
                  {/* Backdrop image — subtle by default, brighter on hover */}
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none opacity-20 group-hover:opacity-45 transition-opacity duration-500"
                    style={{
                      backgroundImage: `url('${bd.img}')`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                  {/* Readability wash (top-down white → transparent) */}
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(180deg, hsl(var(--card) / 0.85) 0%, hsl(var(--card) / 0.55) 45%, hsl(var(--card) / 0.25) 100%)",
                    }}
                  />
                  {/* Hover glow ring inside the card */}
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 rounded-[inherit] pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      boxShadow: `inset 0 0 0 1px rgba(var(--card-glow), 0.35)`,
                    }}
                  />

                  {/* Card content sits above overlays */}
                  <div className="relative z-10 flex flex-col h-full">
                    <div className="icon-tile rounded-2xl aspect-[4/3] flex items-center justify-center backdrop-blur-sm">
                      <Icon className="w-12 h-12 text-foreground" strokeWidth={1.2} />
                    </div>
                    <h3 className="mt-6 font-heading font-bold text-xl leading-tight">{s.name}</h3>
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1 line-clamp-4">{s.tagline}</p>
                    <div className="mt-4 w-10 h-10 rounded-full border border-border flex items-center justify-center bg-background/70 backdrop-blur-sm group-hover:bg-secondary group-hover:text-secondary-foreground group-hover:border-secondary transition-colors">
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-4">
          <Link to="/services" className="text-sm font-medium text-primary inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="services-view-all">
            View all services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
